import { useRef, type RefObject } from 'react';
import { AnimatePresence, motion, useDragControls, useReducedMotion, type PanInfo } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { ease } from '../../../core/format/motion';
import { AdminNavList } from './AdminNavList';
import { AdminAccount } from './AdminAccount';
import { useModalLock } from './useModalLock';
import type { AdminNavItem } from '../../adminNav';
import type { NavCounts } from './useNavCounts';
type Props = {
  open: boolean;
  onClose: () => void;
  items: AdminNavItem[];
  /** De pagina erachter: wordt onbereikbaar (inert) zolang het menu open is. */
  pageRef: RefObject<HTMLElement | null>;
  onLogout: () => void;
  counts?: NavCounts;
};

/** Zo ver naar beneden vegen (of zo snel) sluit het menu. */
const CLOSE_OFFSET_PX = 96;
const CLOSE_VELOCITY = 500;

/**
 * Het volledige menu op de telefoon, als paneel van onderaf: daar zit de knop die het opent
 * (Menu in de tabbalk), en daar zit je duim. Naar beneden vegen, de achtergrond of Escape sluit.
 */
export function AdminMenuSheet({
  open,
  onClose,
  items,
  pageRef,
  onLogout,
  counts
}: Props) {
  const reduce = useReducedMotion();
  const panelRef = useRef<HTMLDivElement>(null);
  const controls = useDragControls();
  useModalLock(open, panelRef, pageRef, onClose);
  const onDragEnd = (_: unknown, info: PanInfo) => {
    if (info.offset.y > CLOSE_OFFSET_PX || info.velocity.y > CLOSE_VELOCITY) onClose();
  };
  return <AnimatePresence data-id="element-5236" data-mp="1f2twj0gaasv6">
      {open && <div className="lg:hidden" data-id="element-5237" data-mp="1k0c8kzpllo7e">
          <motion.div className="fixed inset-0 z-overlay bg-ink/30" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease
      }} onClick={onClose} aria-hidden="true" data-id="element-5238" data-mp="1k1euet82d0gg" />
          <motion.div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby="admin-menu-title" className="fixed inset-x-0 bottom-0 z-overlay flex max-h-[88dvh] flex-col rounded-t-2xl bg-sand shadow-xl" initial={reduce ? {
        opacity: 0
      } : {
        y: '100%'
      }} animate={reduce ? {
        opacity: 1
      } : {
        y: 0
      }} exit={reduce ? {
        opacity: 0
      } : {
        y: '100%'
      }} transition={{
        duration: 0.26,
        ease
      }} drag={reduce ? false : 'y'} dragControls={controls} dragListener={false} dragConstraints={{
        top: 0,
        bottom: 0
      }} dragElastic={{
        top: 0,
        bottom: 0.6
      }} onDragEnd={onDragEnd} data-id="element-5239" data-mp="30dtzedgon55b">
            {/* Greep + kop: hier vegen sluit; de lijst eronder scrolt gewoon. */}
            <div className="shrink-0 touch-none px-4 pb-2 pt-2" onPointerDown={e => !reduce && controls.start(e)} data-id="element-5240" data-mp="vf438163yybx">
              <span className="mx-auto block h-1 w-10 rounded-full bg-line" aria-hidden="true" data-id="element-5241" data-mp="2woyinmg16qs" />
              <div className="mt-2 flex items-center justify-between" data-id="element-5242" data-mp="3u3j9peav3rll">
                <h2 id="admin-menu-title" className="px-2 font-display text-[1.4rem] font-light text-ink" data-id="element-5243" data-mp="3u16vnc8861dt">
                  Menu
                </h2>
                <button type="button"
            // Het menu opent op een tik; de sluitknop is dan de eerste logische stop.
            autoFocus onClick={onClose} aria-label="Menu sluiten" className="flex h-11 w-11 items-center justify-center rounded-md text-muted hover:text-ink" data-id="element-5244" data-mp="2ec7en7glk3xp">
                  <XIcon className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" data-id="element-5245" data-mp="2xbbhfyhjp1gx" />
                </button>
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain" data-id="element-5246" data-mp="2jasdb2g7afk1">
              <AdminNavList items={items} variant="sheet" counts={counts} data-id="element-5247" data-mp="2o8tt9eibxyra" />
            </div>
            <AdminAccount onLogout={onLogout} showSiteLink data-id="element-5248" data-mp="17uu0qoltdbe2" />
          </motion.div>
        </div>}
    </AnimatePresence>;
}